import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
    userLookup,
    registerStudentWithContact,
    getOfficeAndProductTerms,
} from './wizardApi';
import * as validator from './validator';
import axios from "axios";

/**
 * Store for managing the booking wizard state
 * Handles all wizard steps, validation, and API interactions
 */

export const STEP_USER_SELECT = 1;
export const STEP_REGISTER = 2;
export const STEP_ADDITIONAL_ATTENDEES = 3;
export const STEP_CONFIRM_PAY = 4;

export const useWizardStore = defineStore('wizardStore', () => {
    // Current step tracking
    const currentStep = ref(STEP_USER_SELECT);
    const completedSteps = ref([]);
    const enabledSteps = ref([STEP_USER_SELECT]);


    // Step 1: User Type
    const userType = ref(null); // 0 = self, 2 = on behalf of someone else

    // Step 2: Registration
    // Student information
    const isLoadingStudent = ref(false);
    const manuallyRegisterStudent = ref(false);
    const showStudentDetailFields = ref(false);

    const initialUser = {
        firstName:           '',
        lastName:            '',
        address:             '',
        postalCode:          '',
        city:                '',
        email:               '',
        mobile:              '',
        fnr:                 '',
        type:                null, // 0 = self, 2 = on behalf of someone else
        errors:              {},
        lookupResponse:      {},
        lookupId:            null,
        userId:              null,
        deliveryOption:      null,
        withInterpreter:     false,
        comment:             '',
        resourcePoolClassId: 0,
    }
    const user = ref({ ...initialUser });
    const student = ref({ ...initialUser });
    student.value.errors = {};
    student.value.lookupResponse = {};
    const contact = ref({ ...initialUser });
    contact.value.errors = {};
    contact.value.lookupResponse = {};
    const additionalStudent = ref({ ...initialUser });
    additionalStudent.value.errors = {};
    additionalStudent.value.lookupResponse = {};

    // Contact information (when booking for someone else)
    const isLoadingContact = ref(false);
    const manuallyRegisterContact = ref(false);
    const showContactDetailFields = ref(false);

    // Additional information (when booking for someone else)
    const isLoadingAdditionalStudent = ref(false);
    const manuallyRegisterAdditionalStudent = ref(false);
    const showAdditionalStudentDetailFields = ref(false);

    // Registration status
    const isRegistering = ref(false);
    const registerError = ref('');
    const isRegistered = ref(false);

    // Step 4: Add additional attendees
    const additionalAttendees = ref([]);

    // Step 5: Confirm and pay
    const termsAccepted = ref(false);
    const paymentMethod = ref(null);
    const isProcessingPayment = ref(false);
    const paymentError = ref('');

    // Tailwind classes
    const tw = ref(false);
    // Booking metadata
    const classId = ref(null);
    const yskLocationId = ref(null);
    const officeId = ref(null);
    const eventStartTime = ref(null);
    const nnidPlaceholder = ref(''); // Placeholder for Norwegian national ID format
    const productId = ref(null);
    const courseId = ref(null);
    const start = ref(false);
    const end = ref(false);
    const teacherId = ref(null);
    const packageId = ref(null);
    const productType = ref(null);
    const followUpId = ref(null);
    const freeInterpreterSlots = ref(0);
    const freeSlots = ref(0);
    const isDeliverable = ref(false);
    const bookingTerms = ref('');
    const hasFullBookingTerms = ref(false);
    const termsPageUrl = ref('');
    const hasApprovedBookingTerms = ref(false);
    const productTerms = ref([]);
    const availableResourcePools = ref([]);
    const followUpDetails = ref([]);
    const followUpCompleted = ref(false);
    const isCompletingFollowup = ref(false);
    const followUpError = ref('')
    const deliveryOptions = ref([])
    const selectedDeliveryOption = ref(null);
    const deliveryError = ref('')

    const paymentCompleted = ref(false);
    const netsCheckoutEndpoint = ref(null);

    const $reset = () => {
        //reset steps
        currentStep.value = STEP_USER_SELECT;
        completedSteps.value = [];
        enabledSteps.value = [STEP_USER_SELECT];

        // Reset users
        user.value = { ...initialUser };
        student.value = { ...initialUser };
        student.value.errors = {};
        student.value.lookupResponse = {};
        contact.value = { ...initialUser };
        contact.value.errors = {};
        contact.value.lookupResponse = {};
        additionalStudent.value = { ...initialUser };
        additionalStudent.value.errors = {};
        additionalStudent.value.lookupResponse = {};

        isLoadingStudent.value = false
        manuallyRegisterStudent.value = false
        showStudentDetailFields.value = false

        // Contact information (when booking for someone else)
        isLoadingContact.value = false
        manuallyRegisterContact.value = false
        showContactDetailFields.value = false

        // Additional information (when booking for someone else)
        isLoadingAdditionalStudent.value = false
        manuallyRegisterAdditionalStudent.value = false
        showAdditionalStudentDetailFields.value = false

        // Registration status
        isRegistering.value = false
        registerError.value = ''
        isRegistered.value = false

        // Step 3: Add additional attendees
        additionalAttendees.value = []

        // Step 4: Confirm and pay
        termsAccepted.value = false
        paymentMethod.value = null
        isProcessingPayment.value = false
        paymentError.value = ''
        paymentCompleted.value = false
    }


    // Computed properties

    const hasResourcePool = computed(() => {
        return availableResourcePools.value && availableResourcePools.value.length > 0;
    });
    const isContact = computed(() => user.value.type === 2);


    const getStudentMobileLabel = computed(() => {
        return isContact.value ? window.TABS.$t('Elevens mobilnummer') : window.TABS.$t('Mobilnummer');
    });

    const getStudentFnrLabel = computed(() => {
        return isContact.value ? window.TABS.$t('Elevens personnummer') : window.TABS.$t('Personnummer');
    });

    const toggleManuallyRegisteredStudentLabel = computed(() => {
        return manuallyRegisterStudent.value ?
            window.TABS.$t('Hent detaljer via fødselsnummer') :
            window.TABS.$t('Registrer opplysninger manuelt');
    });

    const toggleManuallyRegisteredContactLabel = computed(() => {
        return manuallyRegisterContact.value ?
            window.TABS.$t('Hent detaljer via fødselsnummer') :
            window.TABS.$t('Registrer opplysninger manuelt');
    });

    const toggleManuallyRegisterAdditionalStudentLabel = computed(() => {
        return manuallyRegisterAdditionalStudent.value ?
            window.TABS.$t('Hent detaljer via fødselsnummer') :
            window.TABS.$t('Registrer opplysninger manuelt');
    })

    // Actions


    const hasFreeInterpreterSlots = computed(() => {
        if (!freeInterpreterSlots.value || freeInterpreterSlots.value <= 0) {
            return false
        }

        let interpreterCount = student.value.withInterpreter ? 1 : 0;
        additionalAttendees.value.forEach(attendee => {
            if (attendee.withInterpreter) {
                interpreterCount++;
            }
        });
        return interpreterCount < freeInterpreterSlots.value;
    })

    /**
     * Validate student details
     */
    function validateStudent() {
        student.value.errors = {};
        student.value.errors.fnr = validator.validateFnr(student.value.fnr, nnidPlaceholder.value);
        student.value.errors.mobile = validator.validateMobile(student.value.mobile);
        student.value.errors.firstName = validator.validateFirstName(student.value.firstName, student.value.lookupResponse.first_name);
        student.value.errors.lastName = validator.validateLastName(student.value.lastName, student.value.lookupResponse.last_name);
        student.value.errors.address = validator.validateAddress(student.value.address, student.value.lookupResponse.address);
        student.value.errors.postalCode = validator.validatePostalCode(student.value.postalCode, student.value.lookupResponse.postal_code);
        student.value.errors.city = validator.validateCity(student.value.city, student.value.lookupResponse.city);
        student.value.errors.email = validator.validateEmail(student.value.email, student.value.lookupResponse.email);

        deliveryError.value = deliveryOptions.value.length > 0 && !selectedDeliveryOption.value ? window.TABS.$t('Velg leveringsmetode') : '';

        return Object.values(student.value.errors).filter(v => v).length === 0;
    }

    function validateContact() {
        contact.value.errors = {};
        contact.value.errors.fnr = validator.validateFnr(contact.value.fnr, nnidPlaceholder.value);
        contact.value.errors.mobile = validator.validateMobile(contact.value.mobile);
        contact.value.errors.firstName = validator.validateFirstName(contact.value.firstName, contact.value.lookupResponse.first_name);
        contact.value.errors.lastName = validator.validateLastName(contact.value.lastName, contact.value.lookupResponse.last_name);
        contact.value.errors.address = validator.validateAddress(contact.value.address, contact.value.lookupResponse.address);
        contact.value.errors.postalCode = validator.validatePostalCode(contact.value.postalCode, contact.value.lookupResponse.postal_code);
        contact.value.errors.city = validator.validateCity(contact.value.city, contact.value.lookupResponse.city);
        contact.value.errors.email = validator.validateEmail(contact.value.email, contact.value.lookupResponse.email);

        return Object.values(contact.value.errors).filter(v => v).length === 0;
    }

    function validateAdditionalStudent() {
        additionalStudent.value.errors = {};
        additionalStudent.value.errors.fnr = validator.validateFnr(additionalStudent.value.fnr, nnidPlaceholder.value);
        additionalStudent.value.errors.mobile = validator.validateMobile(additionalStudent.value.mobile);
        additionalStudent.value.errors.firstName = validator.validateFirstName(additionalStudent.value.firstName, additionalStudent.value.lookupResponse.first_name);
        additionalStudent.value.errors.lastName = validator.validateLastName(additionalStudent.value.lastName, additionalStudent.value.lookupResponse.last_name);
        additionalStudent.value.errors.address = validator.validateAddress(additionalStudent.value.address, additionalStudent.value.lookupResponse.address);
        additionalStudent.value.errors.postalCode = validator.validatePostalCode(additionalStudent.value.postalCode, additionalStudent.value.lookupResponse.postal_code);
        additionalStudent.value.errors.city = validator.validateCity(additionalStudent.value.city, additionalStudent.value.lookupResponse.city);
        additionalStudent.value.errors.email = validator.validateEmail(additionalStudent.value.email, additionalStudent.value.lookupResponse.email);

        return Object.values(additionalStudent.value.errors).filter(v => v).length === 0;
    }

    function studentHasValidFnrAndMobile() {
        return !validator.validateFnr(student.value.fnr, nnidPlaceholder.value) && !validator.validateMobile(student.value.mobile)
    }

    function contactHasValidFnrAndMobile() {
        return !validator.validateFnr(contact.value.fnr, nnidPlaceholder.value) && !validator.validateMobile(contact.value.mobile)
    }

    function additionalStudentHasValidFnrAndMobile() {
        return !validator.validateFnr(additionalStudent.value.fnr, nnidPlaceholder.value) && !validator.validateMobile(additionalStudent.value.mobile)
    }

    function toggleManuallyRegisterStudent() {
        manuallyRegisterStudent.value = !manuallyRegisterStudent.value;
        showStudentDetailFields.value = manuallyRegisterStudent.value;
        resetStudentLookupResponse();

        if (studentHasValidFnrAndMobile() && !manuallyRegisterStudent.value) {
            triggerStudentLookup();
        }
    }

    function disableManuallyRegisterStudent() {
        manuallyRegisterStudent.value = false;
        showStudentDetailFields.value = manuallyRegisterStudent.value;
        resetStudentLookupResponse();
    }

    function resetStudentLookupResponse() {
        student.value.lookupResponse = {};
        student.value.firstName = '';
        student.value.lastName = '';
        student.value.address = '';
        student.value.postalCode = '';
        student.value.city = '';
        student.value.email = '';
        student.value.lookupId = null;
        student.value.userId = null;
        student.value.deliveryOption = null;
        student.value.withInterpreter = false;
    }

    function toggleManuallyRegisterContact() {
        manuallyRegisterContact.value = !manuallyRegisterContact.value;
        showContactDetailFields.value = manuallyRegisterContact.value;
        resetContactLookupResponse();

        if (contactHasValidFnrAndMobile() && !manuallyRegisterContact.value) {
            triggerContactLookup();
        }
    }

    function disableManuallyRegisterContact() {
        manuallyRegisterContact.value = false;
        showContactDetailFields.value = manuallyRegisterContact.value;
        resetContactLookupResponse();
    }

    function resetContactLookupResponse() {
        contact.value.lookupResponse.value = {};
        contact.value.firstName = '';
        contact.value.lastName = '';
        contact.value.address = '';
        contact.value.postalCode = '';
        contact.value.city = '';
        contact.value.email = '';
        contact.value.lookupId = null;
        contact.value.userId = null;
        contact.value.deliveryOption = null;
        contact.value.withInterpreter = false;
    }

    function resetAdditionalStudentLookupResponse() {
        additionalStudent.value.lookupResponse = {};
        additionalStudent.value.firstName = '';
        additionalStudent.value.lastName = '';
        additionalStudent.value.address = '';
        additionalStudent.value.postalCode = '';
        additionalStudent.value.city = '';
        additionalStudent.value.email = '';
        additionalStudent.value.lookupId = null;
        additionalStudent.value.userId = null;
        additionalStudent.value.deliveryOption = null;
        additionalStudent.value.withInterpreter = false;
    }

    function toggleManuallyRegisterAdditionalStudent() {
        manuallyRegisterAdditionalStudent.value = !manuallyRegisterAdditionalStudent.value;
        showAdditionalStudentDetailFields.value = manuallyRegisterAdditionalStudent.value;
        resetAdditionalStudentLookupResponse();

        if (additionalStudentHasValidFnrAndMobile() && !manuallyRegisterAdditionalStudent.value) {
            triggerAdditionalStudentLookup();
        }
    }

    function disableManuallyRegisterAdditionalStudent() {
        manuallyRegisterAdditionalStudent.value = false;
        showAdditionalStudentDetailFields.value = manuallyRegisterAdditionalStudent.value;
        resetAdditionalStudentLookupResponse();
    }

    function validateAndLookupStudent() {
        student.value.errors.lookup = '';

        if (!studentHasValidFnrAndMobile()) {
            resetStudentLookupResponse();
            return;
        }

        if (manuallyRegisterStudent.value) {
            return;
        }

        triggerStudentLookup();
    }

    function validateAndLookupContact() {
        contact.value.errors.lookup = '';

        if (!contactHasValidFnrAndMobile()) {
            resetContactLookupResponse();
            return;
        }

        if (manuallyRegisterContact.value) {
            return;
        }

        triggerContactLookup();
    }

    function validateAndLookupAdditionalStudent() {
        additionalStudent.value.errors.lookup = '';

        if (!additionalStudentHasValidFnrAndMobile()) {
            resetAdditionalStudentLookupResponse();
            return;
        }

        if (manuallyRegisterAdditionalStudent.value) {
            return;
        }

        triggerAdditionalStudentLookup();
    }

    function triggerStudentLookup() {
        if (isLoadingStudent.value) {
            return;
        }
        student.value.errors = {}
        isLoadingStudent.value = true;
        showStudentDetailFields.value = false;

        const data = {
            fnr:    student.value.fnr,
            mobile: student.value.mobile,
            type:   'student',
        };
        userLookup()
            .post(data)
            .then(response => {
                student.value.lookupResponse = response.data;

                if (response.data.user_id) {
                    student.value.userId = response.data.user_id;
                }

                if (response.data.lookup_id) {
                    student.value.lookupId = response.data.lookup_id;
                }

                if (response.data.user_id || response.data.lookup_id) {
                    // Successfully found user or lookup data
                    student.value.firstName = response.data.first_name || '';
                    student.value.lastName = response.data.last_name || '';
                    student.value.address = response.data.address || '';
                    student.value.postalCode = response.data.postal_code || '';
                    student.value.city = response.data.city || '';
                    student.value.email = response.data.email || '';
                }
                showStudentDetailFields.value = true;
            })
            .catch(error => {
                student.value.errors.lookup = error.body?.message || window.TABS.$t('Det oppstod en feil ved søk etter bruker');
                showStudentDetailFields.value = false;
            })
            .finally(() => {
                isLoadingStudent.value = false;
            });
    }

    /**
     * Trigger contact lookup API call
     */
    function triggerContactLookup() {
        if (isLoadingContact.value) {
            return;
        }

        contact.value.errors = {}
        isLoadingContact.value = true;
        showContactDetailFields.value = false;

        const data = {
            fnr:    contact.value.fnr,
            mobile: contact.value.mobile,
            type:   'contact',
        };
        userLookup()
            .post(data)
            .then(response => {
                contact.value.lookupResponse = response.data;

                if (response.data.user_id) {
                    contact.value.userId = response.data.user_id;
                }

                if (response.data.lookup_id) {
                    contact.value.lookupId = response.data.lookup_id;
                }

                if (response.data.user_id || response.data.lookup_id) {
                    // Successfully found user or lookup data
                    contact.value.firstName = response.data.first_name || '';
                    contact.value.lastName = response.data.last_name || '';
                    contact.value.address = response.data.address || '';
                    contact.value.postalCode = response.data.postal_code || '';
                    contact.value.city = response.data.city || '';
                    contact.value.email = response.data.email || '';
                }

                showContactDetailFields.value = true;
            })
            .catch(error => {
                contact.value.errors.lookup = error.body?.message || window.TABS.$t('Det oppstod en feil ved søk etter bruker');
                showContactDetailFields.value = false;
            })
            .finally(() => {
                isLoadingContact.value = false;
            });
    }

    function triggerAdditionalStudentLookup() {
        if (isLoadingAdditionalStudent.value) {
            return;
        }

        additionalStudent.value.errors = {}
        isLoadingAdditionalStudent.value = true;
        showAdditionalStudentDetailFields.value = false;

        const data = {
            fnr:    additionalStudent.value.fnr,
            mobile: additionalStudent.value.mobile,
            type:   'addStudent',
        };

        userLookup()
            .post(data)
            .then(response => {
                additionalStudent.value.lookupResponse = response.data;

                if (response.data.user_id) {
                    additionalStudent.value.userId = response.data.user_id;
                }

                if (response.data.lookup_id) {
                    additionalStudent.value.lookupId = response.data.lookup_id;
                }

                if (response.data.user_id || response.data.lookup_id) {
                    // Successfully found user or lookup data
                    additionalStudent.value.firstName = response.data.first_name || '';
                    additionalStudent.value.lastName = response.data.last_name || '';
                    additionalStudent.value.address = response.data.address || '';
                    additionalStudent.value.postalCode = response.data.postal_code || '';
                    additionalStudent.value.city = response.data.city || '';
                    additionalStudent.value.email = response.data.email || '';
                }
                showAdditionalStudentDetailFields.value = true;
            })
            .catch(error => {
                additionalStudent.value.errors.lookup = error.body?.message || window.TABS.$t('Det oppstod en feil ved søk etter bruker');
                showAdditionalStudentDetailFields.value = false;
            })
            .finally(() => {
                isLoadingAdditionalStudent.value = false;
            });
    }

    /**
     * Validate and register the user
     */
    function validateAndRegister() {
        let studentIsValid = validateStudent();
        let contactIsValid = true;
        if (isContact.value) {
            contactIsValid = validateContact();
        }

        if (!studentIsValid || !contactIsValid) {
            return false;
        }

        if (isRegistering.value || isLoadingContact.value || isLoadingStudent.value) {
            return false;
        }

        isRegistering.value = true;
        registerError.value = '';

        const data = {
            student_id:          student.value.userId,
            student_lookup_id:   student.value.lookupId,
            student_mobile:      student.value.mobile,
            student_fnr:         student.value.fnr,
            student_first_name:  student.value.firstName,
            student_last_name:   student.value.lastName,
            student_address:     student.value.address,
            student_postal_code: student.value.postalCode,
            student_city:        student.value.city,
            student_email:       student.value.email,
            user_type:           isContact.value ? 2 : 0,
            class_id:            classId.value,
            ysk_location_id:     yskLocationId.value,
            event_start:         eventStartTime.value,
        };

        if (isContact.value) {
            data.contact_user_id = contact.value.userId;
            data.contact_lookup_id = contact.value.lookupId;
            data.contact_mobile = contact.value.mobile;
            data.contact_fnr = contact.value.fnr;
            data.contact_first_name = contact.value.firstName;
            data.contact_last_name = contact.value.lastName;
            data.contact_address = contact.value.address;
            data.contact_postal_code = contact.value.postalCode;
            data.contact_city = contact.value.city;
            data.contact_email = contact.value.email;
        }

        return registerStudentWithContact(officeId.value)
            .post(data)
            .then(response => {
                isRegistered.value = true;
                student.value.userId = response.data.student_id;
                contact.value.userId = response.data.contact_user_id || null;
                completeStep(STEP_REGISTER);
                if (!courseId.value) {
                    completeStep(STEP_ADDITIONAL_ATTENDEES);
                }
                return true;
            })
            .catch(error => {
                registerError.value = error.body?.message || window.TABS.$t('Det oppstod en feil ved registrering');
                return false;
            })
            .finally(() => {
                isRegistering.value = false;
            });
    }

    function validateAndRegisterAdditionalStudent() {

        let studentIsValid = validateAdditionalStudent();
        let contactIsValid = true;
        if (isContact.value) {
            contactIsValid = validateContact();
        }

        if (!studentIsValid || !contactIsValid) {
            return false;
        }

        if (isRegistering.value || isLoadingContact.value || isLoadingStudent.value) {
            return false;
        }

        isRegistering.value = true;
        registerError.value = '';

        const data = {
            student_id:          additionalStudent.value.userId,
            student_lookup_id:   additionalStudent.value.lookupId,
            student_mobile:      additionalStudent.value.mobile,
            student_fnr:         additionalStudent.value.fnr,
            student_first_name:  additionalStudent.value.firstName,
            student_last_name:   additionalStudent.value.lastName,
            student_address:     additionalStudent.value.address,
            student_postal_code: additionalStudent.value.postalCode,
            student_city:        additionalStudent.value.city,
            student_email:       additionalStudent.value.email,
            user_type:           isContact.value ? 2 : 0,
            class_id:            classId.value,
            ysk_location_id:     yskLocationId.value,
            event_start:         eventStartTime.value,
        };

        if (isContact.value) {
            data.contact_user_id = contact.value.userId;
            data.contact_lookup_id = contact.value.lookupId;
            data.contact_mobile = contact.value.mobile;
            data.contact_fnr = contact.value.fnr;
            data.contact_first_name = contact.value.firstName;
            data.contact_last_name = contact.value.lastName;
            data.contact_address = contact.value.address;
            data.contact_postal_code = contact.value.postalCode;
            data.contact_city = contact.value.city;
            data.contact_email = contact.value.email;
        }

        return registerStudentWithContact(officeId.value)
            .post(data)
            .then(response => {
                addAttendee({
                    firstName:           additionalStudent.value.firstName,
                    lastName:            additionalStudent.value.lastName,
                    userId:              response.data.student_id,
                    withInterpreter:     additionalStudent.value.withInterpreter,
                    deliveryOption:      additionalStudent.value.deliveryOption,
                    resourcePoolClassId: additionalStudent.value.resourcePoolClassId,
                })
                additionalStudent.value = { ...initialUser };
                updateTotalPrice();
                return true;
            })
            .catch(error => {
                registerError.value = error.body?.message || window.TABS.$t('Det oppstod en feil ved registrering');
                return false;
            })
            .finally(() => {
                isRegistering.value = false;
            });
    }

    /**
     * Add additional attendee to the booking
     */
    function addAttendee(attendee) {
        if (additionalAttendees.value.filter(a => a.userId === attendee.userId).length > 0) {
            // student already added
            return;
        }
        additionalAttendees.value.push(attendee);
    }

    /**
     * Remove attendee from the booking
     */
    function removeAttendee(index) {
        additionalAttendees.value.splice(index, 1);
        updateTotalPrice()
    }

    function getStudentIds() {
        const studentIds = [];
        additionalAttendees.value.forEach(attendee => {
            if (attendee.userId) {
                studentIds.push(attendee.userId);
            }
        });
        return studentIds;
    }

    function getInterpreterIds() {
        const interpreterIds = [];
        if (student.value.withInterpreter) {
            interpreterIds.push(student.value.userId);
        }
        additionalAttendees.value.forEach(attendee => {
            if (attendee.withInterpreter && attendee.userId) {
                interpreterIds.push(attendee.userId);
            }
        });
        return interpreterIds;
    }

    const getResourcePoolClassIds = computed(() => {
        const resourcePoolClassIds = {};
        if (student.value.resourcePoolClassId) {
            resourcePoolClassIds[student.value.userId] = student.value.resourcePoolClassId;
        }
        additionalAttendees.value.forEach(attendee => {
            if (attendee.resourcePoolClassId) {
                resourcePoolClassIds[attendee.userId] = attendee.resourcePoolClassId;
            }
        });
        return resourcePoolClassIds;
    })

    const getResourcePoolClassIdsArray = computed(() => {
        const resourcePoolClassIds = [];
        if (student.value.resourcePoolClassId) {
            resourcePoolClassIds[student.value.userId] = student.value.resourcePoolClassId;
        }
        additionalAttendees.value.forEach(attendee => {
            if (attendee.resourcePoolClassId) {
                resourcePoolClassIds[attendee.userId] = attendee.resourcePoolClassId;
            }
        });
        return resourcePoolClassIds;
    });

    function updateTotalPrice() {
        const data = new FormData;
        data.append('userId', student.value.userId);
        data.append('studentIds', getStudentIds());
        data.append('courseId', courseId.value);
        data.append('classId', classId.value);
        data.append('productType', productType.value);
        data.append('officeId', officeId.value);

        axios.post(TABS.api.getEndPoint('optima-total-price'), data)
            .then(response => response.data)
            .then(res => {
                if (!res.success) {
                    return;
                }
                document.getElementById('product-booking-detail').outerHTML = res.html;
            });
    }

    /**
     * Complete a step and move to the next
     */
    function completeStep(step) {
        if (!completedSteps.value.includes(step)) {
            completedSteps.value.push(step);
        }
        // Enable next step
        const nextStep = step + 1;
        if (!enabledSteps.value.includes(nextStep)) {
            enabledSteps.value.push(nextStep);
        }

        // Move to next step
        currentStep.value = nextStep;
    }

    function resetFromStep(step) {
        // Reset completed steps from the given step onwards
        completedSteps.value = completedSteps.value.filter(s => s < step);

        // Reset enabled steps to include only those up to the given step
        enabledSteps.value = enabledSteps.value.filter(s => s <= step);

        // Reset current step to the given step
        currentStep.value = step;

        // Fallthrough is on purpose to reset state for steps 1 and 2 when going back and altering step 1
        if (step === STEP_USER_SELECT) {
            student.value = { ...initialUser };
            student.value.errors = {};
            student.value.lookupResponse = {};
            contact.value = { ...initialUser };
            contact.value.errors = {};
            contact.value.lookupResponse = {};
        }

    }

    function getTerms() {
        return getOfficeAndProductTerms(officeId.value)
            .post({
                officeId:    officeId.value,
                classId:     classId.value,
                productId:   productId.value,
                courseId:    courseId.value,
                start:       start.value,
                end:         end.value,
                teacherId:   teacherId.value,
                packageId:   packageId.value,
                productType: productType.value,
                followUpId:  followUpId.value,
            })
            .then(response => {
                if (response.data) {
                    productTerms.value = response.data.productTerms;
                    bookingTerms.value = response.data.bookingTerms;
                    hasFullBookingTerms.value = response.data.hasFullBookingTerms;
                    termsPageUrl.value = response.data.termsPageUrl;
                    availableResourcePools.value = response.data.resourcePools;
                    followUpDetails.value = response.data.followUpDetails || [];
                    deliveryOptions.value = response.data.deliveryOptions || [];
                    netsCheckoutEndpoint.value = response.data.netsCheckoutEndpoint || null;

                    if (typeof Dibs === 'undefined') {
                        var script = document.createElement('script');
                        script.src = netsCheckoutEndpoint.value + '/v1/checkout.js?v=1';
                        document.head.appendChild(script);
                    }

                    if (deliveryOptions.value.length === 1) {
                        selectedDeliveryOption.value = deliveryOptions.value[0].value;
                    }
                }
            })
            .catch(error => {
                console.error('Error fetching terms:', error);
            });
    }

    function completeFollowUp() {
        followUpError.value = '';
        if (!followUpId.value) {
            followUpError.value = window.TABS.$t('Kunne ikke fullføre påmeldingen.');
            return Promise.resolve();
        }

        isCompletingFollowup.value = true;

        try {
            TABS.booking.request('do_booking', null, function (response) {
                isCompletingFollowup.value = false;
                if (response && (response.success || response.status === 'ok')) {
                    followUpCompleted.value = true;
                    return;
                }
                followUpCompleted.value = false;
                followUpError.value = (response.message || window.TABS.$t('Det oppstod en feil ved påmeldingen'));
            }, {
                follow_up_id:   followUpId.value,
                username:       student.value.mobile,
                fnr:            student.value.fnr,
                terms_approved: hasApprovedBookingTerms.value ? 1 : 0,
                userId:         student.value.userId,
                officeId:       officeId.value,
                comment:        student.value.comment,
            });
        } catch (error) {
            isCompletingFollowup.value = false;
            followUpCompleted.value = false;
            followUpError.value = window.TABS.$t('Det oppstod en feil ved påmeldingen');
        }
    }

    return {
        // State
        tw,
        currentStep,
        completedSteps,
        enabledSteps,
        user,
        userType,
        student,
        isLoadingStudent,
        manuallyRegisterStudent,
        showStudentDetailFields,
        isLoadingContact,
        manuallyRegisterContact,
        showContactDetailFields,
        contact,
        isLoadingAdditionalStudent,
        manuallyRegisterAdditionalStudent,
        showAdditionalStudentDetailFields,
        additionalStudent,
        isRegistering,
        registerError,
        isRegistered,
        additionalAttendees,
        termsAccepted,
        paymentMethod,
        isProcessingPayment,
        paymentError,
        classId,
        yskLocationId,
        officeId,
        eventStartTime,
        productId,
        courseId,
        start,
        end,
        teacherId,
        packageId,
        productType,
        followUpId,
        freeInterpreterSlots,
        freeSlots,
        isDeliverable,
        bookingTerms,
        hasFullBookingTerms,
        termsPageUrl,
        hasApprovedBookingTerms,
        productTerms,
        availableResourcePools,
        paymentCompleted,
        followUpDetails,
        isCompletingFollowup,
        followUpCompleted,
        followUpError,
        deliveryOptions,
        selectedDeliveryOption,
        deliveryError,
        netsCheckoutEndpoint,

        // Computed
        isContact,
        nnidPlaceholder,
        getStudentMobileLabel,
        getStudentFnrLabel,
        toggleManuallyRegisteredStudentLabel,
        toggleManuallyRegisteredContactLabel,
        toggleManuallyRegisterAdditionalStudentLabel,
        hasResourcePool,
        hasFreeInterpreterSlots,
        getResourcePoolClassIds,
        getResourcePoolClassIdsArray,

        // Actions
        toggleManuallyRegisterStudent,
        toggleManuallyRegisterContact,
        toggleManuallyRegisterAdditionalStudent,
        resetStudentLookupResponse,
        resetContactLookupResponse,
        resetAdditionalStudentLookupResponse,
        studentHasValidFnrAndMobile,
        additionalStudentHasValidFnrAndMobile,
        contactHasValidFnrAndMobile,
        validateAndLookupStudent,
        validateAndLookupContact,
        validateAndLookupAdditionalStudent,
        validateAndRegister,
        validateAndRegisterAdditionalStudent,
        removeAttendee,
        completeStep,
        resetFromStep,
        disableManuallyRegisterStudent,
        disableManuallyRegisterContact,
        disableManuallyRegisterAdditionalStudent,
        getTerms,
        getStudentIds,
        getInterpreterIds,
        completeFollowUp,

        $reset,
    };
});